/**
 * Phone compass: "which way is this phone pointing", in degrees TRUE — used to set a static
 * camera's heading by pointing the phone the way the lens points.
 *
 * "Pointing" = where the back camera looks when the phone is held up like taking a photo, or where
 * the top edge points when the phone lies flat (the two blend at ~30° of tilt).
 *
 * Sources, best first:
 *  - 'native'   Airview Studio iOS app (lib/native.ts): CoreMotion/CoreLocation true heading.
 *  - 'ios-web'  Safari: DeviceOrientationEvent.webkitCompassHeading (magnetic → true with WMM).
 *               Needs DeviceOrientationEvent.requestPermission() from a tap: call start() in a click.
 *  - 'absolute' Android Chrome: deviceorientationabsolute alpha/beta/gamma (magnetic → true).
 *
 * Reference-counted: every start() needs a matching stop().
 */
import { useEffect, useSyncExternalStore } from 'react';
import { declinationAt } from '@apos/shared';
import { getNative, onNative, type NativeHeading } from './native';

export type CompassStatus = 'off' | 'requesting' | 'active' | 'denied' | 'unsupported';
export type CompassSource = 'native' | 'ios-web' | 'absolute';

export interface CompassState {
  status: CompassStatus;
  enabled?: boolean;
  /** Degrees true, 0–360; null until the first reading. */
  headingDeg: number | null;
  /** ± degrees when the platform reports it. */
  accuracyDeg: number | null;
  source: CompassSource | null;
  t: number | null;
  /**
   * 0 = lying flat … 1 = held upright like taking a photo (uprightFromEuler); null when the
   * platform does not say (the native app sends only the heading). Round 4: the facing is shared
   * with the crew only while the phone is not lying flat.
   */
  upright?: number | null;
}

const D = Math.PI / 180;
const norm = (d: number) => ((d % 360) + 360) % 360;

/**
 * Compass heading (°, clockwise from the alpha reference) of where the phone points, from W3C
 * DeviceOrientation Euler angles (Z-X'-Y'', degrees). Back camera axis when tilted up past ~30°,
 * top edge when flat.
 */
export function headingFromEuler(alpha: number, beta: number, gamma: number): number {
  const ca = Math.cos(alpha * D), sa = Math.sin(alpha * D);
  const cb = Math.cos(beta * D), sb = Math.sin(beta * D);
  const cg = Math.cos(gamma * D), sg = Math.sin(gamma * D);
  // back-camera direction (device −Z) in the earth frame (x east, y north, z up)
  const bx = -ca * sg - sa * sb * cg;
  const by = -sa * sg + ca * sb * cg;
  // top-edge direction (device +Y)
  const tx = -sa * cb;
  const ty = ca * cb;
  const bh = Math.hypot(bx, by);
  const w = Math.max(0, Math.min(1, (bh - 0.35) / 0.3)); // 0 flat … 1 upright
  const x = bx * w + tx * (1 - w);
  const y = by * w + ty * (1 - w);
  return norm(Math.atan2(x, y) / D);
}

/**
 * How upright the phone is (0 flat … 1 upright, the same blend headingFromEuler uses): the length
 * of the back-camera axis projected on the ground, from beta/gamma (alpha does not change it).
 */
export function uprightFromEuler(beta: number, gamma: number): number {
  const sb = Math.sin(beta * D), cg = Math.cos(gamma * D), sg = Math.sin(gamma * D);
  const bh = Math.sqrt(sg * sg + sb * sb * cg * cg);
  return Math.max(0, Math.min(1, (bh - 0.35) / 0.3));
}

type Listener = () => void;

export class Compass {
  private state: CompassState = { status: 'off', headingDeg: null, accuracyDeg: null, source: null, t: null };
  private listeners = new Set<Listener>();
  private refs = 0;
  private pinned = false;
  private suppressed = false;
  private detach: (() => void) | null = null;
  private pos: { lat: number; lon: number } | null = null;

  getState = (): CompassState => this.state;
  subscribe = (fn: Listener): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  /** Latest phone position — used to turn magnetic into true heading. */
  setPosition(p: { lat: number; lon: number } | null): void {
    if (p) this.pos = p;
  }

  private emit(patch: Partial<CompassState>): void {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l();
  }

  private toTrue(magneticDeg: number): number {
    const p = this.pos;
    return p ? norm(magneticDeg + declinationAt(p.lat, p.lon, Date.now())) : norm(magneticDeg);
  }

  /** Begin updates. Call from a user gesture on the first use (iOS Safari permission). */
  async start(): Promise<void> {
    this.refs++;
    if (this.suppressed) return;
    if (this.detach || this.state.status === 'requesting') return;
    const native = getNative();
    if (native) {
      this.emit({ status: 'requesting' });
      const off = onNative<NativeHeading>('heading', (h) => {
        const deg = h.trueDeg ?? this.toTrue(h.magneticDeg);
        this.emit({ status: 'active', headingDeg: norm(deg), accuracyDeg: h.accuracyDeg != null && h.accuracyDeg >= 0 ? h.accuracyDeg : null, source: 'native', t: h.t, upright: h.upright ?? null });
      });
      native.startHeading();
      this.detach = () => {
        off();
        native.stopHeading();
      };
      return;
    }
    if (typeof window === 'undefined' || typeof DeviceOrientationEvent === 'undefined') {
      this.emit({ status: 'unsupported' });
      return;
    }
    const DOE = DeviceOrientationEvent as unknown as { requestPermission?: () => Promise<'granted' | 'denied'> };
    if (typeof DOE.requestPermission === 'function') {
      this.emit({ status: 'requesting' });
      try {
        if ((await DOE.requestPermission()) !== 'granted') {
          this.emit({ status: 'denied' });
          return;
        }
      } catch {
        this.emit({ status: 'denied' });
        return;
      }
    }
    if (this.refs === 0) {
      this.emit({ status: 'off' }); // stopped while asking
      return;
    }
    const onIos = (e: DeviceOrientationEvent) => {
      const w = e as DeviceOrientationEvent & { webkitCompassHeading?: number; webkitCompassAccuracy?: number };
      if (typeof w.webkitCompassHeading === 'number' && !Number.isNaN(w.webkitCompassHeading)) {
        const acc = w.webkitCompassAccuracy;
        const upright = e.beta != null && e.gamma != null ? uprightFromEuler(e.beta, e.gamma) : null;
        this.emit({ status: 'active', headingDeg: this.toTrue(w.webkitCompassHeading), accuracyDeg: acc != null && acc >= 0 ? acc : null, source: 'ios-web', t: Date.now(), upright });
      }
    };
    const onAbs = (e: DeviceOrientationEvent) => {
      if (e.alpha == null || e.beta == null || e.gamma == null) return;
      this.emit({ status: 'active', headingDeg: this.toTrue(headingFromEuler(e.alpha, e.beta, e.gamma)), accuracyDeg: null, source: 'absolute', t: Date.now(), upright: uprightFromEuler(e.beta, e.gamma) });
    };
    window.addEventListener('deviceorientation', onIos);
    window.addEventListener('deviceorientationabsolute', onAbs as EventListener);
    if (this.state.status !== 'active') this.emit({ status: 'requesting' });
    // no reading within 3 s → this device has no compass
    const timer = setTimeout(() => {
      if (this.state.headingDeg === null && this.detach) this.emit({ status: 'unsupported' });
    }, 3000);
    this.detach = () => {
      clearTimeout(timer);
      window.removeEventListener('deviceorientation', onIos);
      window.removeEventListener('deviceorientationabsolute', onAbs as EventListener);
    };
  }

  /** An explicit user choice survives route and component lifetimes. */
  async enable(): Promise<void> {
    this.suppressed = false;
    try { sessionStorage.setItem('sightline:compass-enabled', '1'); } catch { /* unavailable */ }
    if (this.pinned && this.detach) return;
    if (!this.pinned) { this.pinned = true; this.emit({ enabled: true }); }
    await this.start();
    this.stop(); // the pin, rather than a component reference, owns this session
  }

  restore(): void {
    let preference: string | null = null;
    try { preference = sessionStorage.getItem('sightline:compass-enabled'); } catch { /* unavailable */ }
    // Native sensors need no Safari motion-permission gesture. Pin them for the
    // signed-in session so Team, Point and camera navigation cannot stop heading.
    if (preference === '1' || (preference !== '0' && !compassNeedsTap())) void this.enable();
  }

  disable(): void {
    this.suppressed = true;
    try { sessionStorage.setItem('sightline:compass-enabled', '0'); } catch { /* unavailable */ }
    this.detach?.(); this.detach = null;
    this.emit({ status: 'off', headingDeg: null, t: null });
    this.pinned = false; this.emit({ enabled: false });
    this.shutdownIfIdle();
  }

  /** Sign-out must release sensors without carrying a previous user's preference. */
  reset(): void {
    this.disable();
    try { sessionStorage.removeItem('sightline:compass-enabled'); } catch { /* unavailable */ }
  }

  stop(): void {
    this.refs = Math.max(0, this.refs - 1);
    this.shutdownIfIdle();
  }

  private shutdownIfIdle(): void {
    if (this.refs > 0 || this.pinned) return;
    this.detach?.();
    this.detach = null;
    this.emit({ status: this.state.status === 'denied' || this.state.status === 'unsupported' ? this.state.status : 'off' });
  }
}

export const compass = new Compass();

/** A compass reading is "aimed" when it is recent and the phone is not lying flat. */
export const AIM_MAX_READING_AGE_MS = 3000;

/**
 * The facing to share with the crew right now, or null: the compass is running, its reading is
 * under 3 s old and the phone is not lying flat (upright > 0, or the platform does not say).
 * Accuracy rides along; the map/coverage decide what is accurate enough (≤ 25°).
 */
export function aimedFacing(s: CompassState, now: number = Date.now()): { deg: number; accuracyDeg: number | null; t: number } | null {
  if (s.status !== 'active' || s.headingDeg == null || s.t == null) return null;
  if (now - s.t > AIM_MAX_READING_AGE_MS || s.t - now > AIM_MAX_READING_AGE_MS) return null;
  if (s.upright != null && s.upright <= 0) return null;
  return { deg: s.headingDeg, accuracyDeg: s.accuracyDeg, t: s.t };
}

/** iOS Safari: the compass needs DeviceOrientationEvent.requestPermission() from a tap first. */
export function compassNeedsTap(): boolean {
  return !getNative() && typeof DeviceOrientationEvent !== 'undefined' && typeof (DeviceOrientationEvent as unknown as { requestPermission?: unknown }).requestPermission === 'function';
}

/** Current compass state. Does not start the compass (start it from a tap with compass.start()). */
export function useCompassState(): CompassState {
  return useSyncExternalStore(compass.subscribe, compass.getState, compass.getState);
}

/**
 * Keeps the compass running while `active` (stops on unmount). On iOS Safari the first start must
 * come from a tap — pair this with a button that calls compass.start() once, or rely on the
 * native app, which needs no permission prompt.
 */
export function useCompass(active: boolean): CompassState {
  useEffect(() => {
    if (!active) return;
    void compass.start();
    return () => compass.stop();
  }, [active]);
  return useCompassState();
}
